import React, { useEffect, useState } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { Coffee, Check, Loader2, AlertTriangle, ExternalLink, Sparkles, MessageCircle, Unlink } from "lucide-react";
import Navbar from "../components/Navbar";
import { Button } from "../components/ui/button";
import { useAuth } from "../context/AuthContext";
import { api, auth } from "../lib/api";
import { toast } from "sonner";

const PLAN_BENEFITS = {
  weekly: ["Live breakout screener", "Analyst-vetted BUY alerts", "Full audit trail", "Push + email + Discord"],
  monthly: ["Everything in Weekly", "Save ~25% vs weekly", "Discord subscriber channel", "Cancel anytime"],
};

export default function Account() {
  const { user, setUser } = useAuth();
  const isAdmin = user?.role === "admin";
  const [plans, setPlans] = useState([]);
  const [sub, setSub] = useState(null); // /payments/subscription state
  const [discordStatus, setDiscordStatus] = useState(null); // {linked, in_guild, needs_relink}
  const [busy, setBusy] = useState(false);
  const [adminMode, setAdminMode] = useState(false); // hidden one-time toggle (admin only)
  const [params] = useSearchParams();
  const nav = useNavigate();

  const refreshSub = () => api.get("/payments/subscription").then((r) => setSub(r.data)).catch(() => {});

  // Live-verify Discord guild membership so we can nudge users who linked
  // their account but never actually joined the server (the OAuth bug we
  // shipped a fix for in Feb 2026).
  const refreshDiscordStatus = () =>
    api.get("/account/discord/status").then((r) => setDiscordStatus(r.data)).catch(() => {});

  const refreshMe = () => api.get("/auth/me").then((r) => {
    if (r.data) { auth.setUser(r.data); setUser && setUser(r.data); }
  }).catch(() => {});

  useEffect(() => {
    api.get("/payments/plans").then((r) => setPlans(r.data.plans)).catch(() => {});
    refreshSub();
    refreshDiscordStatus();
    const sid = params.get("session_id");
    const cancelled = params.get("cancelled");
    const discord = params.get("discord");
    const discordErr = params.get("error");
    if (cancelled) {
      toast.info("Checkout cancelled. No charge was made.");
      nav("/app/account", { replace: true });
    } else if (sid) {
      pollStatus(sid);
    } else if (discord === "linked") {
      toast.success("✓ Discord linked. Subscriber role granted.");
      refreshMe();
      refreshDiscordStatus();
      nav("/app/account", { replace: true });
    } else if (discordErr) {
      // Specific hint when the join itself failed (vs. the earlier generic
      // "oauth error"); the user needs to know their link isn't functional.
      if (String(discordErr).startsWith("guild_join_failed")) {
        toast.error("Discord linked, but we couldn't add you to the server. Click Link again — if it keeps failing, message support.", { duration: 7000 });
      } else {
        toast.error(`Discord link failed: ${discordErr.replace(/_/g, " ")}`);
      }
      refreshDiscordStatus();
      nav("/app/account", { replace: true });
    }
  }, []);

  const pollStatus = async (sessionId, attempts = 0) => {
    if (attempts >= 6) {
      toast.error("Payment status check timed out. If charged, your account will activate shortly.");
      return;
    }
    setBusy(true);
    try {
      const r = await api.get(`/payments/status/${sessionId}`);
      if (r.data.payment_status === "paid") {
        if (r.data.access_token) auth.setToken(r.data.access_token);
        if (r.data.user) {
          auth.setUser(r.data.user);
          setUser && setUser(r.data.user);
        }
        toast.success("✓ Payment confirmed — Moonbucks unlocked!");
        nav("/app/account", { replace: true });
        await refreshSub();
        setTimeout(() => window.location.reload(), 800);
        return;
      }
      if (r.data.status === "expired") {
        toast.error("Checkout session expired.");
        return;
      }
      setTimeout(() => pollStatus(sessionId, attempts + 1), 2000);
    } catch (e) {
      toast.error("Could not verify payment.");
    } finally {
      setBusy(false);
    }
  };

  const startCheckout = async (planId) => {
    setBusy(true);
    try {
      const body = { plan_id: planId, origin_url: window.location.origin };
      if (adminMode) body.mode = "onetime";
      const r = await api.post("/payments/checkout", body);
      if (r.data.url) {
        window.location.href = r.data.url;
      } else {
        toast.error("Could not start checkout");
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Checkout failed");
    } finally {
      setBusy(false);
    }
  };

  const openPortal = async () => {
    setBusy(true);
    try {
      const r = await api.post("/payments/portal");
      if (r.data.url) {
        window.location.href = r.data.url;
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not open Customer Portal");
    } finally {
      setBusy(false);
    }
  };

  const linkDiscord = async () => {
    setBusy(true);
    try {
      const r = await api.get("/auth/discord/login");
      if (r.data.url) window.location.href = r.data.url;
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not start Discord link");
    } finally {
      setBusy(false);
    }
  };

  const unlinkDiscord = async () => {
    if (!confirm("Unlink Discord? You'll lose access to subscriber channels.")) return;
    setBusy(true);
    try {
      await api.post("/auth/discord/unlink");
      toast.success("Discord unlinked.");
      await refreshMe();
    } catch (e) {
      toast.error("Unlink failed.");
    } finally {
      setBusy(false);
    }
  };

  const subscribed = sub?.subscribed ?? (user?.role === "admin" || user?.role === "subscriber" || user?.subscribed === true);
  const status = sub?.subscription_status;
  const periodEnd = sub?.current_period_end ? new Date(sub.current_period_end) : null;
  const cancelAtPE = sub?.cancel_at_period_end;
  const paymentFailedAt = sub?.payment_failed_at;
  const hasStripe = sub?.has_stripe_customer;

  return (
    <div className="bg-mb-sand min-h-screen">
      <Navbar />
      <div className="max-w-5xl mx-auto px-6 lg:px-12 py-10">
        <div className="label-uppercase text-mb-emerald">Account</div>
        <h1 className="font-display font-semibold text-4xl tracking-tight mt-1" data-testid="account-title">Your Moonbucks</h1>

        <div className="mt-8 bg-white border border-mb-border rounded-lg p-6" data-testid="account-info-card">
          <div className="grid sm:grid-cols-3 gap-4">
            <Info label="Name" val={user?.name} />
            <Info label="Email" val={user?.email} />
            <Info label="Status" val={subscribed ? "Active" : "No subscription"} highlight={subscribed} />
          </div>
        </div>

        {/* Soft-churn warning — card failed but we're still subscribed during Stripe retry window */}
        {subscribed && paymentFailedAt && status === "past_due" && (
          <div className="mt-6 p-5 rounded-lg bg-amber-50 border-2 border-amber-300" data-testid="payment-failed-banner">
            <div className="flex items-start gap-3">
              <AlertTriangle className="w-6 h-6 text-amber-600 flex-shrink-0 mt-0.5" />
              <div className="flex-1">
                <h3 className="font-display font-bold text-lg text-amber-900">Your payment didn&apos;t go through</h3>
                <p className="mt-1 text-sm text-amber-800 leading-relaxed">
                  Stripe will retry your card a few times over the next 2-3 weeks. You&apos;re still subscribed during that window — but if all retries fail, alerts will pause. Update your card now to avoid interruption.
                </p>
                <Button onClick={openPortal} disabled={busy} className="mt-3 bg-amber-600 hover:bg-amber-700 text-white rounded-md" data-testid="update-card-btn">
                  {busy ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <ExternalLink className="w-4 h-4 mr-2" />}
                  Update payment method
                </Button>
              </div>
            </div>
          </div>
        )}

        {/* Cancel-at-period-end notice */}
        {subscribed && cancelAtPE && periodEnd && (
          <div className="mt-6 p-5 rounded-lg bg-blue-50 border border-blue-200" data-testid="canceling-banner">
            <p className="text-sm text-blue-900">
              <strong>Subscription canceling on {periodEnd.toLocaleDateString(undefined, {month: 'long', day: 'numeric', year: 'numeric'})}.</strong>{" "}
              You&apos;ll keep full access until then. Changed your mind?{" "}
              <button onClick={openPortal} className="underline font-semibold" data-testid="resubscribe-btn">Reactivate in the portal.</button>
            </p>
          </div>
        )}

        {/* Active subscription card */}
        {subscribed && !cancelAtPE && status !== "past_due" && (
          <div className="mt-6 p-6 rounded-lg bg-white border-2 border-mb-emerald" data-testid="active-sub-card">
            <div className="flex items-start gap-3">
              <Check className="w-6 h-6 text-mb-emerald flex-shrink-0 mt-0.5" />
              <div className="flex-1">
                <h3 className="font-display font-semibold text-xl">You&apos;re brewing.</h3>
                <p className="text-sm text-neutral-600 mt-1">
                  Alerts hit your dashboard the moment they&apos;re published.
                  {periodEnd && <span> Next renewal: <span className="font-mono-data font-semibold">{periodEnd.toLocaleDateString()}</span>.</span>}
                  {sub?.plan && <span> Plan: <span className="font-mono-data font-semibold capitalize">{sub.plan}</span>.</span>}
                </p>
              </div>
            </div>
            {hasStripe && (
              <div className="mt-4 pt-4 border-t border-neutral-100">
                <Button onClick={openPortal} disabled={busy} variant="outline" className="rounded-md" data-testid="manage-sub-btn">
                  {busy ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <ExternalLink className="w-4 h-4 mr-2" />}
                  Manage subscription
                </Button>
                <span className="ml-3 text-xs text-neutral-500">Update card · Cancel · View invoices</span>
              </div>
            )}
          </div>
        )}

        {/* Discord community link — only shown to subscribers */}
        {subscribed && (
          <div className="mt-6 p-6 rounded-lg bg-white border border-mb-border" data-testid="discord-link-card">
            <div className="flex items-start gap-3">
              <MessageCircle className="w-6 h-6 text-[#5865F2] flex-shrink-0 mt-0.5" />
              <div className="flex-1">
                <h3 className="font-display font-semibold text-xl">Discord — subscriber channels</h3>
                {user?.discord_id ? (
                  <>
                    <p className="text-sm text-neutral-600 mt-1">
                      Linked to <span className="font-mono-data font-semibold">{user.discord_username || user.discord_id}</span>.
                      {discordStatus?.in_guild === false ? (
                        <span className="font-semibold text-amber-700"> You're not in the server yet — rejoin below.</span>
                      ) : (
                        <> You have the <span className="font-semibold">Subscriber</span> role — access to
                          <span className="font-mono-data"> #steady-alerts</span>,
                          <span className="font-mono-data"> #moonshot-alerts</span>,
                          <span className="font-mono-data"> #outcomes</span>,
                          <span className="font-mono-data"> #preview</span>.
                        </>
                      )}
                    </p>
                    {discordStatus?.in_guild === false && (
                      <div
                        className="mt-3 p-3 rounded-md border bg-amber-50 border-amber-200 text-sm text-amber-900"
                        data-testid="discord-rejoin-nudge"
                      >
                        <div className="font-semibold">Your account is linked but Discord didn't finish adding you to the Moonbucks server.</div>
                        <div className="text-xs mt-1 opacity-90">
                          This was a bug we just patched — hit Rejoin to force Discord to drop you in and apply the Subscriber role.
                        </div>
                        <Button
                          onClick={linkDiscord}
                          disabled={busy}
                          className="mt-3 rounded-md font-bold text-white hover:opacity-90"
                          style={{ backgroundColor: "#5865F2" }}
                          data-testid="discord-rejoin-btn"
                        >
                          {busy ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <MessageCircle className="w-4 h-4 mr-2" />}
                          Rejoin Moonbucks server
                        </Button>
                      </div>
                    )}
                    <Button onClick={unlinkDiscord} disabled={busy} variant="outline" className="mt-4 rounded-md" data-testid="discord-unlink-btn">
                      {busy ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Unlink className="w-4 h-4 mr-2" />}
                      Unlink Discord
                    </Button>
                  </>
                ) : (
                  <>
                    <p className="text-sm text-neutral-600 mt-1">
                      Link your Discord account and get instant access to subscriber-only channels — live alerts, desk notes, and outcomes hit your Discord seconds after the algo publishes.
                    </p>
                    <Button
                      onClick={linkDiscord}
                      disabled={busy}
                      className="mt-4 rounded-md font-bold text-white hover:opacity-90"
                      style={{ backgroundColor: "#5865F2" }}
                      data-testid="discord-link-btn"
                    >
                      {busy ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <MessageCircle className="w-4 h-4 mr-2" />}
                      Link Discord
                    </Button>
                  </>
                )}
              </div>
            </div>
          </div>
        )}

        {/* INTERNAL — admin one-time toggle. Always visible to admin (even when already subscribed)
            so analyst desk can issue promo / lifetime / gift charges at any time. */}
        {isAdmin && (
          <div className="mt-6 p-3 rounded-xl bg-neutral-900 text-white text-xs" data-testid="admin-onetime-toggle">
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="checkbox"
                checked={adminMode}
                onChange={(e) => setAdminMode(e.target.checked)}
                className="accent-mb-emerald"
                data-testid="admin-onetime-checkbox"
              />
              <Sparkles className="w-3.5 h-3.5" />
              <span className="font-mono-data tracking-wider">INTERNAL — One-time charge mode (promo · lifetime · gift)</span>
            </label>
          </div>
        )}

        {/* Plan selection — for non-subscribers + admin in one-time mode */}
        {(!subscribed || (isAdmin && adminMode)) && (
          <>
            <div className="mt-6 grid md:grid-cols-2 gap-4" data-testid="plan-cards">
              {plans.map((p) => (
                <div
                  key={p.id}
                  className={`rounded-lg p-6 border-2 ${p.id === "monthly" ? "bg-mb-emerald text-white border-mb-emerald" : "bg-white border-mb-border"}`}
                  data-testid={`plan-card-${p.id}`}
                >
                  <div className={`label-uppercase ${p.id === "monthly" ? "text-white/60" : "text-mb-emerald"}`}>
                    {p.label}
                  </div>
                  <div className="mt-4 flex items-baseline gap-2">
                    <span className="font-mono-data text-5xl font-extrabold">${p.amount}</span>
                    <span className={p.id === "monthly" ? "text-white/70" : "text-neutral-500"}>
                      /{p.interval}
                    </span>
                  </div>
                  <p className={`mt-1 text-xs ${p.id === "monthly" ? "text-white/70" : "text-neutral-500"}`}>
                    {p.id === "weekly" ? "~$0.71/day · cheaper than a black coffee" : "Save ~25% · skip 3 lattes"}
                  </p>

                  {/* Configured indicator — gives clear UX when env vars not set */}
                  {!p.configured && !adminMode && (
                    <p className={`mt-2 text-[11px] font-mono-data ${p.id === "monthly" ? "text-amber-200" : "text-amber-700"}`} data-testid={`plan-not-configured-${p.id}`}>
                      Subscription pricing configuring — coming online soon.
                    </p>
                  )}

                  <ul className="mt-5 space-y-2 text-sm">
                    {(PLAN_BENEFITS[p.id] || []).map((b) => (
                      <li key={b} className="flex items-center gap-2">
                        <Coffee className={`w-4 h-4 ${p.id === "monthly" ? "text-white" : "text-mb-emerald"}`} />
                        {b}
                      </li>
                    ))}
                  </ul>
                  <Button
                    onClick={() => startCheckout(p.id)}
                    disabled={busy || (!p.configured && !adminMode)}
                    className={`w-full mt-6 rounded-md font-bold ${p.id === "monthly" ? "bg-white text-mb-emerald hover:bg-neutral-100" : "bg-mb-emerald text-white hover:bg-mb-emerald-dark"}`}
                    data-testid={`subscribe-btn-${p.id}`}
                  >
                    {busy ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
                    {adminMode
                      ? `Charge once — $${p.amount}`
                      : `Subscribe — $${p.amount}/${p.interval === "week" ? "wk" : "mo"}`}
                  </Button>
                  <p className={`mt-3 text-[10px] text-center ${p.id === "monthly" ? "text-white/60" : "text-neutral-400"}`}>
                    {adminMode ? "ONE-TIME · internal" : "Recurring · cancel anytime · secured by Stripe"}
                  </p>
                </div>
              ))}
            </div>
          </>
        )}
      </div>
    </div>
  );
}

const Info = ({ label, val, highlight }) => (
  <div>
    <div className="label-uppercase text-neutral-500">{label}</div>
    <div className={`mt-1 font-display font-bold text-xl ${highlight ? "text-mb-emerald" : ""}`}>{val}</div>
  </div>
);
